<template>
  <v-layout column>
    <v-dialog v-model="showMonthPickerDialog"
              persistent
              max-width="450">
      <v-card class="pa-2">
        <v-toolbar>
          <v-toolbar-side-icon>
            <v-icon color="primary">event</v-icon>
          </v-toolbar-side-icon>
          <v-toolbar-title class="text--primary">
            Select Month
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <month-picker v-model="dialogMonthYear"
                        no-default
                        :default-year="currentYearNumber()">
          </month-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="accent" flat @click="pickMonthYear">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-toolbar extended extension-height="75" class="pb-2">
      <v-layout v-if="history">
        <v-btn flat label color="accent" @click="showMonthPickerDialog = true" class="pl-0 ml-0">
          <v-icon class="pr-2">event</v-icon>
          {{ selectedMonthYear ? (selectedMonthYear.month + ', ' + selectedMonthYear.year) : 'Select Month' }}
        </v-btn>
      </v-layout>
      <v-toolbar-title v-else>{{ title }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn-toggle v-if="customerLoadCount || carrierLoadCount || groupedLoadCount ||
       groupLoadsByCustomer || groupLoadsByCarrier || customGroupedItems">
        <v-btn v-model="groupLoadsByCustomer"
               v-if="customerLoadCount || groupLoadsByCustomer"
               :color="groupLoadsByCustomer ? 'green' : null">
          <v-icon :color="groupLoadsByCustomer ? 'white' : null">
            face
          </v-icon>
        </v-btn>
        <v-btn v-model="groupLoadsByCarrier"
               v-if="carrierLoadCount || groupLoadsByCarrier"
               :color="groupLoadsByCarrier ? 'accent' : null">
          <v-icon v-if="dispatched" :color="groupLoadsByCarrier ? 'white' : null">
            share
          </v-icon>
          <v-icon v-else :color="groupLoadsByCarrier ? 'white' : null">
            local_shipping
          </v-icon>
        </v-btn>

        <v-btn v-model="customGroupedItems"
               v-if="groupedLoadCount || customGroupedItems"
               :color="customGroupedItems ? 'primary' : null">
          <v-icon :color="customGroupedItems ? 'white' : null">
            {{customGroupIcon}}
          </v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-layout slot="extension" column>
        <div class="pa-0 ma-0 text-right align-center" v-if="accurateSince">
          <v-layout row>
            <v-chip label light
                    class="font-weight-light">
              {{ accurateSince }}
              <v-btn v-if="history"
                     fab small flat
                     color="accent"
                     :disabled="!selectedMonthYear"
                     @click="$emit('loadMonth', selectedMonthYear)">
                <v-icon>refresh</v-icon>
              </v-btn>
              <v-btn v-else
                     fab small flat
                     color="accent"
                     @click="$emit('refresh')">
                <v-icon>refresh</v-icon>
              </v-btn>
            </v-chip>
          </v-layout>
        </div>

        <v-layout slot="extension" justify-end align-end>
          <v-text-field :disabled="history ? !selectedMonthYear : (!loads.length && !searchTerm)"
                        v-model="searchTerm"
                        append-icon="search"
                        class="single-line hide-details pb-0 mb-0"
                        @input="$emit('update:searchTerm', searchTerm)"
                        :label="searchPrompt">
          </v-text-field>
        </v-layout>
      </v-layout>
    </v-toolbar>

    <v-card pl-4
            class="pt-4"
            :class="{'side-list-desktop': !isMobile}">
      <v-layout v-if="loading" >
        <slot name="loading">
          <v-progress-circular indeterminate
                               color="primary"
                               class="mx-auto">
          </v-progress-circular>
        </slot>
      </v-layout>

      <v-list two-line v-else>
        <slot name="subheader">
          <v-subheader v-if="subheader">
            <v-icon class="pr-2" color="primary" v-if="subheaderIcon">
              {{ subheaderIcon }}
            </v-icon>
            {{ subheader }} <span v-if="subheaderCount != null">({{subheaderCount}})</span>
          </v-subheader>
        </slot>

        <template v-for="(load, idx) in loads" v-if="!groupLoadsByCustomer && !groupLoadsByCarrier && !customGroupedItems">
          <slot name="loadTile" :load="load" :idx="idx">
            fallback
          </slot>
        </template>

        <template v-for="(loads, sponsor, idx) in customerLoads" v-if="groupLoadsByCustomer && customerLoadCount">
          <slot name="customerLoadsTile" :loads="loads" :sponsor="sponsor" :idx="idx">
            customer loads fallback
          </slot>
        </template>

        <template v-for="(loads, carrier, idx) in carrierLoads" v-if="groupLoadsByCarrier && carrierLoads != null">
          <slot name="carrierLoadsTile" :loads="loads" :carrier="carrier" :idx="idx">
            carrier loads fallback
          </slot>
        </template>

        <template v-for="(items, item, idx) in groupedItems" v-if="customGroupedItems && groupedItems != null">
          <slot name="groupedItemsTile" :items="items" :item="item" :idx="idx">
            Custom items slot
          </slot>
        </template>
      </v-list>

    </v-card>
  </v-layout>
</template>

<script>
import { MonthPicker, MonthPickerInput } from 'vue-month-picker';
import moment from 'moment';

export default {
  name: 'VLoadNavList',
  components: {
    MonthPicker,
    MonthPickerInput,
  },
  props: {
    loads: {
      type: Array,
      required: true,
    },
    groupedItems: {
      type: Object,
      default: null,
    },
    customerLoads: {
      type: Object,
      default: null,
    },
    carrierLoads: {
      type: Object,
      default: null,
    },
    history: {
      type: Boolean,
      default: false,
    },
    dispatched: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    searchPrompt: {
      type: String,
      default: () => 'Search loads',
    },
    loading: {
      type: Boolean,
      default: () => null,
    },
    accurateSince: {
      type: String,
    },
    subheader: {
      type: String,
    },
    subheaderCount: {
      type: Number,
    },
    subheaderIcon: {
      type: String,
    },
    customGroupIcon: {
      type: String,
      default: () => 'linear_scale',
    },
  },
  data: () => ({
    selectedMonthYear: null,
    dialogMonthYear: null,
    showMonthPickerDialog: false,

    searchTerm: null,
    selected: null,
    groupLoadsByCustomer: false,
    groupLoadsByCarrier: false,
    customGroupedItems: false,
  }),
  computed: {
    customerLoadCount() {
      return !!this.customerLoads;
    },
    carrierLoadCount() {
      return !!this.carrierLoads;
    },
    groupedLoadCount() {
      return !!this.groupedItems;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    pickMonthYear() {
      if (this.isValidMonthYear(this.dialogMonthYear)) {
        if (!this.selectedMonthYear ||
          this.selectedMonthYear.month !== this.dialogMonthYear.month ||
          this.selectedMonthYear.year !== this.dialogMonthYear.year) {
          this.selectedMonthYear = this.dialogMonthYear;

          if (this.selectedMonthYear) {
            // if the user doesn't select one, the month picker doesn't update the v-model. so, null.
            this.$emit('selectMonth', this.selectedMonthYear);
          }
        }

        this.showMonthPickerDialog = false;
      } else {
        this.$store.dispatch('applications/setWarningNotification', {
          message: 'Must select a month not in the future',
        });
      }
    },
    isValidMonthYear(chosenMY) {
      if (!chosenMY) {
        return false;
      }

      // http://momentjs.com/docs/#/query/
      const now = moment().date(1);
      const chosenMoment = moment().year(chosenMY.year).month(chosenMY.monthIndex - 1).date(1);

      const yay = now.isSameOrAfter(chosenMoment, 'date');
      return yay;
    },
    currentMonthNumber() {
      return moment().month() + 1;
    },
    currentYearNumber() {
      return moment().year();
    },
  },
  created() {
  },
};
</script>

<style>
  .side-list-desktop {
    min-height: calc(100vh - 325px);
    max-height: calc(100vh - 325px);
    overflow: auto;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/simple/LoadNavList.vue